import { toPng } from "html-to-image";
import Cookies from "js-cookie";
import jsPDF from "jspdf";
import parsePhoneNumberFromString, { CountryCode } from "libphonenumber-js";
import moment from "moment";
import { toast } from "react-toastify";
import {
  CreatedEvent,
  CreateEvent,
  DiscountType,
  TicketType,
} from "../redux/features/event/event.types";
import { PayoutCurrency } from "../redux/features/payment/payment.types";
import {
  adminAuthTokenCookieKey,
  APP_PORT,
  APP_SCHEME,
  clientAuthTokenCookieKey,
  COOKIE_DOMAIN,
  currencyShorts,
  isLocalhost,
  MAIN_DOMAIN,
  PRODUCT_PRICING,
  salesAuthTokenCookieKey,
} from "./constants";
import { DateFilter } from "./types";

export const setCookie = (name: string, value: string): void => {
  Cookies.set(name, value, {
    expires: 7,
    path: "/",
    domain: COOKIE_DOMAIN,
    secure: true,
    sameSite: "None",
  });
};

export const getCookie = (name: string): string | undefined => {
  const value = Cookies.get(name);
  return value;
};

export const removeCookie = (name: string): void => {
  Cookies.remove(name, {
    expires: 1,
    path: "/",
    domain: COOKIE_DOMAIN,
    secure: true,
    sameSite: "None",
  });
};

const DEFAULT_ERROR_MESSAGE = "An error occurred";
const SENSITIVE_ERROR_PATTERNS = [
  /api\s*key/i,
  /secret/i,
  /sk_(test|live)_/i,
  /stripe/i,
  /paystack/i,
  /token/i,
  /credential/i,
];

export const getSafeErrorMessage = (
  error: any,
  fallback = DEFAULT_ERROR_MESSAGE,
) => {
  const message =
    error?.error?.data?.message ??
    error?.data?.message ??
    error?.message ??
    fallback;

  if (
    typeof message !== "string" ||
    SENSITIVE_ERROR_PATTERNS.some((pattern) => pattern.test(message))
  ) {
    return fallback;
  }

  return message;
};

export const handleError = (
  error: any,
  toastError: boolean = false,
  fallback = DEFAULT_ERROR_MESSAGE,
) => {
  if (toastError) {
    toast.error(getSafeErrorMessage(error, fallback));
  }
};

export const isClientAuthCookieSet = (): boolean => {
  const authCookie = getCookie(clientAuthTokenCookieKey);

  return authCookie !== undefined;
};

export const isAdminAuthCookieSet = (): boolean => {
  const authCookie = getCookie(adminAuthTokenCookieKey);

  return authCookie !== undefined;
};

export const isSalesAuthCookieSet = (): boolean => {
  const authCookie = getCookie(salesAuthTokenCookieKey);
  return authCookie !== undefined;
};

export const normalizeDate = (label: string) => {
  if (!label) return label;

  // Format Week 40, 2025"
  const formatWeekDate = label.match(/Week\s(\d{1,2}),\s(\d{4})/);
  if (formatWeekDate) {
    const [, weekNumber, year] = formatWeekDate;

    const startOfWeek = moment()
      .year(Number(year))
      .isoWeek(Number(weekNumber))
      .startOf("isoWeek");
    const endOfWeek = moment(startOfWeek).endOf("isoWeek");

    return `${startOfWeek.format("MMM DD")} - ${endOfWeek.format("MMM DD")}`;
  }

  return label;
};

export const formatDate = (
  date: string | Date | moment.Moment | undefined | null,
  format: string = "MMMM DD, YYYY",
  ignoreISOTime: boolean = false,
  uppercase: boolean = false,
): string => {
  try {
    if (!date) {
      return "";
    }
    let dateVal = date;

    if (typeof dateVal === "string" && ignoreISOTime) {
      dateVal = dateVal.split("T")[0];
    }

    const momentDate = moment(dateVal);

    if (!momentDate.isValid()) {
      throw new Error("Invalid date");
    }

    let formattedDate = momentDate.format(format);
    if (uppercase) {
      formattedDate = formattedDate.toUpperCase();
    }

    return formattedDate;
  } catch (error) {
    return "Invalid date";
  }
};

export const formatTime = (
  time: string | Date | moment.Moment | undefined | null,
  format: string = "h:mm A",
): string => {
  try {
    if (!time) {
      return "";
    }
    const momentTime = moment(time);
    if (!momentTime.isValid()) {
      throw new Error("Invalid time");
    }
    return momentTime.format(format);
  } catch (error) {
    return "Invalid time";
  }
};

export const parseTimeToDate = (
  time: string,
  format: string = "h:mm A",
): Date | null => {
  try {
    const momentTime = moment(time.trim(), [format, "hh:mm A"], true);
    if (!momentTime.isValid()) {
      throw new Error("Invalid time format");
    }
    return momentTime.toDate();
  } catch (error) {
    console.log(time, error);
    return null;
  }
};

export const parseDateStringToDate = (
  dateString?: string,
  format?: string,
  strict?: boolean,
): Date | null => {
  try {
    if (!dateString) {
      return null;
    }

    const momentDate = moment(dateString, format, strict);
    if (!momentDate.isValid()) {
      throw new Error("Invalid date format");
    }
    return momentDate.toDate();
  } catch (error) {
    return null;
  }
};

export const formatToCurrency = (
  amount: number | string,
  {
    decimalPlaces = 2,
    currency = "NGN",
  }: {
    decimalPlaces?: number;
    currency?: string;
  },
) => {
  return formatNumber(amount, {
    decimalPlaces: decimalPlaces,
    style: !!currency ? "currency" : "decimal",
    currency: currency,
  });
};

export const formatNumber = (
  amount: number | string,
  {
    decimalPlaces = 2,
    style = "decimal",
    currency,
  }: {
    decimalPlaces?: number;
    style?: keyof Intl.NumberFormatOptionsStyleRegistry;
    currency?: string;
  } = {},
) => {
  if (amount === Infinity) {
    return "∞";
  }

  let amountStr = "";

  amountStr = `${String(amount).split(".")[0]}.${
    String(amount).split(".")[1] ?? "00"
  }`;

  amountStr = amountStr.replace(/[^\d.]/g, "");

  const numericAmount = parseFloat(amountStr);

  if (isNaN(numericAmount)) {
    return "";
  }

  let result = "";

  const formatter = new Intl.NumberFormat("en-NG", {
    style: style,
    currency: currency,
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });

  result = formatter.format(
    style === "percent" ? numericAmount / 100 : numericAmount,
  );

  return result.replace(".00", "");
};

export const formatPercentage = (amount: number | string) => {
  if (amount === Infinity) {
    return "∞";
  }

  const numericAmount = Number(String(amount).replace(/[^\d.-]/g, "") ?? 0);

  if (isNaN(numericAmount)) {
    return "Invalid amount"; // Return a default message or handle the error as needed
  }

  const formatter = new Intl.NumberFormat("en-NG", {
    style: "percent",
    trailingZeroDisplay: "stripIfInteger",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return formatter.format(numericAmount / 100).replace(".00", "");
};

export const parseStringToNumber = (
  rawString?: string | number,
): number | null => {
  try {
    const numericString =
      String(rawString ?? "").replace(/[^\d.-]/g, "") !== ""
        ? String(rawString ?? "").replace(/[^\d.-]/g, "")
        : "0";

    const numericValue = parseFloat(numericString);

    if (isNaN(numericValue)) {
      throw new Error("Invalid naira format");
    }

    return numericValue;
  } catch (error) {
    return null;
  }
};

export const removeElementAtIndex = <T>(
  arrayOriginal: T[],
  index: number,
): T[] => {
  const array = JSON.parse(JSON.stringify(arrayOriginal));
  // Check if the index is within bounds
  if (index >= 0 && index < array.length) {
    array.splice(index, 1);
  }
  return array;
};

export const deepCopy = <T extends object>(item: T): T => {
  return JSON.parse(JSON.stringify(item));
};

export const moveElement = <T>(
  array: T[],
  startIndex: number,
  endIndex: number,
): T[] => {
  const arrayCopy = JSON.parse(JSON.stringify(array));
  const [element] = arrayCopy.splice(startIndex, 1);
  arrayCopy.splice(endIndex, 0, element);
  return arrayCopy;
};

export const copyTextToClipboard = async (text: string): Promise<void> => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (error) {
    console.error("Failed to copy text to clipboard", error);
  }
};

export const getEventUrl = (slug: string) => {
  return isLocalhost
    ? `${APP_SCHEME}//${MAIN_DOMAIN}:${APP_PORT}/${slug}`
    : `${APP_SCHEME}//${MAIN_DOMAIN}/${slug}`;
};

export const getTimeAgo = (timeString: string): string => {
  const time = new Date(timeString).getTime();
  const now = Date.now();
  const secondsAgo = Math.floor((now - time) / 1000);

  if (secondsAgo < 60) {
    return `${secondsAgo} seconds ago`;
  }

  const minutesAgo = Math.floor(secondsAgo / 60);
  if (minutesAgo < 60) {
    return `${minutesAgo} minutes ago`;
  }

  const hoursAgo = Math.floor(minutesAgo / 60);
  if (hoursAgo < 24) {
    return `${hoursAgo} hours ago`;
  }

  const daysAgo = Math.floor(hoursAgo / 24);
  return `${daysAgo} days ago`;
};

export const getTimeToGo = (timeString: string): string => {
  const time = new Date(timeString).getTime();
  const now = Date.now();

  const secondsToGo = Math.floor((time - now) / 1000);
  if (secondsToGo < 0) {
    return "past";
  }

  if (secondsToGo < 60) {
    return `${secondsToGo} seconds`;
  }

  const minutesToGo = Math.floor(secondsToGo / 60);
  if (minutesToGo < 60) {
    return `${minutesToGo} minutes`;
  }

  const hoursToGo = Math.floor(minutesToGo / 60);
  if (hoursToGo < 24) {
    return `${hoursToGo} hours`;
  }

  const daysTogo = Math.floor(hoursToGo / 24);
  return `${daysTogo} days`;
};

export const calcFees = (
  total: number,
  currency: PayoutCurrency,
  discountValue?: number,
  discountType?: DiscountType,
  ticketCount: number = 0, // Total number of tickets that the price exceed MIN_TICKET_PRICE_FOR_FIXED_FEE_CHARGE
  eligibleTicketPrice?: number, // Price of the single ticket the discount applies to
) => {
  let totalTicketPrice = total;

  const feePercentage = PRODUCT_PRICING[currency].feePercentage;
  const fixedFeeCharge = PRODUCT_PRICING[currency].fixedFeeCharge;
  const minTicketPriceForFixedFeeCharge =
    PRODUCT_PRICING[currency].minTicketPriceForFixedFeeCharge;

  if (discountValue && discountType) {
    const basePrice = eligibleTicketPrice ?? total;
    if (discountType === "percentage") {
      totalTicketPrice = total - (basePrice * discountValue) / 100;
    } else if (discountType === "amount") {
      totalTicketPrice = total - Math.min(discountValue, basePrice);
    }
  }

  let fixedChargeMultiplier = 0;

  if (ticketCount > 0 && totalTicketPrice > minTicketPriceForFixedFeeCharge) {
    fixedChargeMultiplier = ticketCount;
  } else if (
    ticketCount <= 0 &&
    totalTicketPrice > minTicketPriceForFixedFeeCharge
  ) {
    fixedChargeMultiplier = 1;
  } else {
    fixedChargeMultiplier = 0;
  }

  const totalFees =
    totalTicketPrice * feePercentage + fixedFeeCharge * fixedChargeMultiplier;

  return totalFees > 0 ? totalFees : 0;
};

export const calcTotalTicketPrice = (
  total: number,
  currency: PayoutCurrency,
  discountValue?: number,
  discountType?: DiscountType,
  addFees: boolean = true,
  ticketCount: number = 0, // Total number of tickets that the price exceed MIN_TICKET_PRICE_FOR_FIXED_FEE_CHARGE
  eligibleTicketPrice?: number, // Price of the single ticket the discount applies to
) => {
  let totalTicketPrice = total;

  if (discountValue && discountType) {
    const basePrice = eligibleTicketPrice ?? total;
    if (discountType === "percentage") {
      totalTicketPrice = total - (basePrice * discountValue) / 100;
    } else if (discountType === "amount") {
      totalTicketPrice = total - Math.min(discountValue, basePrice);
    }
  }

  totalTicketPrice = addFees
    ? totalTicketPrice +
      calcFees(total, currency, discountValue, discountType, ticketCount, eligibleTicketPrice)
    : totalTicketPrice;

  return totalTicketPrice > 0 ? totalTicketPrice : 0;
};

export const regexText = (str: string, regexArray: RegExp[]): boolean => {
  return regexArray.some((regex) => regex.test(str));
};

export const getRandomColor = (
  colors: string[],
  selectedColors: string[],
  mode: "sequential" | "random" = "sequential",
): string => {
  if (colors.length === 0) {
    throw new Error("Colors array cannot be empty");
  }

  let randomColor: string;

  if (mode === "random") {
    do {
      randomColor =
        colors[Math.floor(Math.random() * (colors.length - 1 - 0 + 1)) + 0];
    } while (selectedColors.includes(randomColor));
  } else {
    const index = selectedColors.length % colors.length;
    randomColor = colors[index];
  }

  return randomColor;
};

export const handleDownloadPdf = async (
  ref: React.RefObject<HTMLElement | null>,
  docName: string,
) => {
  if (!ref) return;

  const original = ref.current;
  if (!original) return;

  // Clone the element
  const clone = original.cloneNode(true) as HTMLElement;

  clone.style.position = "static";
  clone.style.bottom = "auto";
  clone.style.left = "0";
  clone.style.right = "0";
  clone.style.margin = "0 auto";

  // Create off-screen container
  const offscreen = document.createElement("div");
  offscreen.style.width = "595px"; // Force desktop width
  offscreen.style.transform = "scale(1)";
  offscreen.style.transformOrigin = "top left";
  offscreen.style.position = "absolute";
  offscreen.style.top = "-9999px";
  offscreen.style.left = "-9999px";
  // offscreen.style.width = `${original.offsetWidth}px`;
  offscreen.appendChild(clone);

  document.body.appendChild(offscreen);

  try {
    const dataUrl = await toPng(clone, {
      cacheBust: true,
      width: 595,
      // height: clone.scrollHeight,
      backgroundColor: "#f4f7fa",
    });

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "px",
      format: "a4",
    });

    const img = new Image();
    img.src = dataUrl;

    img.onload = () => {
      pdf.setFillColor(244, 246, 250);
      pdf.rect(
        0,
        0,
        pdf.internal.pageSize.width,
        pdf.internal.pageSize.height,
        "F",
      );

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (img.height * pdfWidth) / img.width;

      pdf.addImage(img, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`${docName}.pdf`);
    };
  } catch (error) {
    console.error("Error generating PDF:", error);
  } finally {
    document.body.removeChild(offscreen);
  }
};

export const handleShare = async (title: string, text: string, url: string) => {
  if (navigator.share) {
    try {
      await navigator.share({
        title: title,
        text: text,
        url: url,
      });
    } catch (error) {
      console.error("Error sharing:", error);
    }
  } else {
    toast.success("Web Share API is not supported in your browser");
  }
};

export const formatDateTime = (
  date: string,
  time: string,
  dateFormat?: string,
  timeFormat?: string,
) => {
  // Get time values
  const parsedTime = parseTimeToDate(time, timeFormat)!;

  // Get date from string, this converts to local time...from utc which the date string comes in
  const mergedDate = parseDateStringToDate(date, dateFormat, !!dateFormat)!;

  // Include time values to date
  mergedDate.setHours(
    parsedTime.getHours(),
    parsedTime.getMinutes(),
    parsedTime?.getSeconds(),
    parsedTime?.getMilliseconds(),
  );

  return formatDate(mergedDate, "yyyy-MM-DD HH:mm:ss ZZZ");
};

export const generateTimeList = (
  stepMinutes: number,
): { label: string; value: Date }[] => {
  const totalMinutesInDay = 24 * 60;
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();
  const date = today.getDate();

  // Number of time slots based on stepMinutes
  const slots = Math.floor(totalMinutesInDay / stepMinutes);

  return Array.from({ length: slots }, (_, i) => {
    const minutes = i * stepMinutes;
    const hour24 = Math.floor(minutes / 60);
    const minute = minutes % 60;

    const period = hour24 < 12 ? "AM" : "PM";
    let hour12 = hour24 % 12;
    if (hour12 === 0) hour12 = 12;

    const minuteStr = minute.toString().padStart(2, "0");
    const label = `${hour12}:${minuteStr} ${period}`;
    const value = new Date(year, month, date, hour24, minute, 0, 0);

    return { label, value };
  });
};

export const getRoundedTimeToNearestXMin = (
  date: Date = new Date(),
  minutes: number = 15,
): string => {
  const now = new Date(date);

  const m = now.getMinutes();
  let roundedMinutes = Math.ceil(m / minutes) * minutes;

  if (roundedMinutes === 60) {
    now.setHours(now.getHours() + 1);
    roundedMinutes = 0;
  }

  let hours = now.getHours();
  const ampm = hours >= 12 ? "PM" : "AM";

  hours = hours % 12;
  if (hours === 0) hours = 12;

  const paddedHours = hours.toString().padStart(2, "0");
  const paddedMinutes = roundedMinutes.toString().padStart(2, "0");

  return `${paddedHours}:${paddedMinutes} ${ampm}`;
};

export const getMonthName = (monthNumber: number): string => {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  if (monthNumber < 1 || monthNumber > 12) {
    throw new Error("Month number must be between 1 and 12");
  }

  return months[monthNumber - 1];
};

export const createEventFromExisting = (
  event: CreatedEvent,
): Partial<CreateEvent> => {
  return {
    name: event.name,
    description: event.description,
    location: event.location,
    visibility: event.visibility,
    category: event.category,
    feePayer: event.feePayer,
    timezone: event.timezone,
    hideLocation: event.hideLocation,
    placeId: event.placeId,
    longitude: event.longitude,
    latitude: event.latitude,
    startDate: event.startDate,
    endDate: event.endDate,
    startTime: event.startTime,
    endTime: event.endTime,
    image: event.image,
    type: event.type,
    status: event.status,
  };
};

export const extractFilenameFromHeader = (header: string | null): string => {
  if (!header) return "export.csv";
  const match = header.match(/filename="?([^"]+)"?/);
  return match?.[1] ?? "export.csv";
};

export const subtractDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() - days);
  return result;
};

export const subtractHoursFromDateTime = (
  dateStr: string,
  timeStr: string,
  hours: number,
): Date => {
  // Parse the time
  const isPM = timeStr.toLowerCase().includes("pm");
  const [hourStr, minuteStr] = timeStr
    .replace(/(am|pm)/i, "")
    .trim()
    .split(":");
  let hour = parseInt(hourStr);
  const minute = parseInt(minuteStr || "0");

  // Convert to 24-hour format
  if (isPM && hour < 12) hour += 12;
  if (!isPM && hour === 12) hour = 0;

  const fullDate = parseDateStringToDate(dateStr)!;
  fullDate.setHours(hour, minute, 0, 0);

  // Subtract hours
  fullDate.setHours(fullDate.getHours() - hours);

  return fullDate;
};

export const formatTimeWithAmPm = (date: Date): string => {
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";

  hours = hours % 12;
  if (hours === 0) hours = 12;

  const hourStr = hours.toString().padStart(2, "0");
  return `${hourStr}:${minutes} ${ampm}`;
};

export function deepEmptyStringsToUndefined<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map(deepEmptyStringsToUndefined) as T;
  } else if (value && typeof value === "object") {
    const result: any = {};
    for (const [key, val] of Object.entries(value)) {
      if (val === "") {
        result[key] = undefined;
      } else {
        result[key] = deepEmptyStringsToUndefined(val);
      }
    }
    return result;
  }
  return value;
}

export const isPhoneNumberValid = (
  number: string,
  countryCode: CountryCode,
) => {
  const cleanedNumber = number.replaceAll(" ", "");

  const isValid = /^\+?\d+$/.test(cleanedNumber);

  if (!isValid) {
    return false;
  }

  const phonePrefixes = ["70", "71", "80", "81", "90", "91"];

  const parsedNumber = parsePhoneNumberFromString(cleanedNumber, countryCode);

  return countryCode === "NG"
    ? parsedNumber?.isValid() &&
        parsedNumber?.nationalNumber?.length === 10 &&
        phonePrefixes.includes(parsedNumber?.nationalNumber?.slice(0, 2))
    : (parsedNumber?.isValid() ?? false);
};

export const parsePhoneNumber = (number: string) => {
  const cleanedNumber = number;

  const isValid = /^\+?\d+$/.test(cleanedNumber);

  if (!isValid) {
    return undefined;
  }

  return parsePhoneNumberFromString(cleanedNumber);
};

export const getEventCurrencyFromTickets = (
  tickets: { currency?: PayoutCurrency; ticketType: TicketType }[],
): PayoutCurrency | undefined => {
  return tickets.filter((t) => t.ticketType === "paid")[0]?.currency;
};

export const getCurrencyShort = (currency?: PayoutCurrency) => {
  return !!currency ? currencyShorts[currency] : undefined;
};

export function formatDateAndTime(dateString: string | null | undefined) {
  if (!dateString) return "";

  const date = new Date(dateString);

  const datePart = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(date);

  let timePart = new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(date);

  timePart = timePart.replace(" ", "");

  return `${datePart} ∙ ${timePart}`;
}
export const getMonthAbbreviation = (monthNumber: string | number): string => {
  const monthNum =
    typeof monthNumber === "string" ? parseInt(monthNumber, 10) : monthNumber;

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  if (monthNum >= 1 && monthNum <= 12) {
    return months[monthNum - 1];
  }

  return "Invalid Month";
};

export const getDisplayRange = (
  filter?: DateFilter,
  startDate?: string,
  endDate?: string,
) => {
  if (filter === "today") return moment().format("MMM D, YYYY");
  if (filter === "yesterday")
    return moment().subtract(1, "day").format("MMM D, YYYY");
  if (filter === "this-week") {
    const startOfWeek = moment().startOf("week");
    const endOfWeek = moment().endOf("week");
    return `${startOfWeek.format("MMM D")} - ${endOfWeek.format("MMM D, YYYY")}`;
  }

  if (filter === "this-month") {
    return moment().format("MMMM YYYY");
  }
  if (filter === "all") return "Jan 2025 - Till Date";
  if (
    (filter === "custom" || (!filter && startDate && endDate)) &&
    startDate &&
    endDate
  ) {
    const start = moment(startDate);
    const end = moment(endDate);
    if (start.isSame(end, "day")) {
      return start.format("MMM D, YYYY");
    }
    return `${start.format("MMM D, YYYY")} - ${end.format("MMM D, YYYY")}`;
  }
  return "Jan 2025 - Till Date";
};

export const SUPPORT_EMAIL_URL =
  "mailto:hello@usetikket.com?subject=Support Request&body=Hi Tikket Team,%0AI need help with...";

export const WHATSAPP_URL =
  "https://wa.me/2349167113768?text=Hi%20Tikket%20Team,%20I%20need%20help%20with...";

export const sendSupportEmail = () => {
  window.open(SUPPORT_EMAIL_URL, "_blank");
};

export const sendWhatsappMessage = () => {
  window.open(WHATSAPP_URL, "_blank");
};

export const getCurrencySymbol = (
  currency: string,
  locale: string = "en-NG",
): string => {
  const parts = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
  }).formatToParts(0);

  const symbol = parts.find((p) => p.type === "currency")?.value;

  if (!symbol) {
    throw new Error(`Invalid currency code: ${currency}`);
  }

  return symbol;
};
